import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllBugs, updateBugStatus, replyToBug, sendBulkEmail, getAllEmails, downloadAttachment } from '../api';
import UserManagement from './UserManagement';
import './AdminDashboard.css';

function AdminDashboard() {
    const [activeTab, setActiveTab] = useState('bugs');
    const [bugs, setBugs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedBug, setSelectedBug] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [isReplying, setIsReplying] = useState(false);
    const [showBulkEmail, setShowBulkEmail] = useState(false);
    const [bulkEmailData, setBulkEmailData] = useState({ subject: '', message: '' });
    const [isSendingBulk, setIsSendingBulk] = useState(false);
    const [allEmails, setAllEmails] = useState([]);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin');
            return;
        }
        fetchBugs();
        fetchEmails();
    }, [navigate, statusFilter]);

    const fetchBugs = async () => {
        setIsLoading(true);
        try {
            const result = await getAllBugs(statusFilter || null);
            setBugs(result);
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('adminToken');
                navigate('/admin');
            } else {
                setError('Failed to fetch bug reports');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fetchEmails = async () => {
        try {
            const result = await getAllEmails();
            setAllEmails(result.emails || []);
        } catch (error) {
            console.error('Failed to fetch emails:', error);
        }
    };

    const handleStatusChange = async (bugId, newStatus) => {
        try {
            await updateBugStatus(bugId, newStatus);
            fetchBugs();
        } catch (error) {
            setError('Failed to update status');
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!selectedBug || !replyMessage.trim()) return;

        setIsReplying(true);
        try {
            await replyToBug(selectedBug.id, replyMessage);
            setReplyMessage('');
            setSelectedBug(null);
            fetchBugs();
        } catch (error) {
            setError('Failed to send reply');
        } finally {
            setIsReplying(false);
        }
    };

    const handleBulkEmail = async (e) => {
        e.preventDefault();
        setIsSendingBulk(true);
        try {
            const emails = selectedEmails.length > 0 ? selectedEmails : null;
            
            const result = await sendBulkEmail(
                bulkEmailData.subject,
                bulkEmailData.message,
                emails
            );
            
            alert(`Email sent successfully!\nTotal: ${result.total}\nSuccess: ${result.success_count}\nFailed: ${result.failed_count}`);
            setShowBulkEmail(false);
            setBulkEmailData({ subject: '', message: '' });
            setSelectedEmails([]);
        } catch (error) {
            setError('Failed to send bulk email');
        } finally {
            setIsSendingBulk(false);
        }
    };

    const handleSelectAllEmails = () => {
        if (selectedEmails.length === allEmails.length) {
            setSelectedEmails([]);
        } else {
            setSelectedEmails([...allEmails]);
        }
    };

    const handleToggleEmail = (email) => {
        if (selectedEmails.includes(email)) {
            setSelectedEmails(selectedEmails.filter(e => e !== email));
        } else {
            setSelectedEmails([...selectedEmails, email]);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        navigate('/admin');
    };

    const getStatusColor = (status) => {
        const colors = {
            submitted: '#F59E0B',
            in_review: '#3B82F6',
            in_progress: '#8B5CF6',
            resolved: '#10B981',
            closed: '#6B7280'
        };
        return colors[status] || '#6B7280';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const handleDownload = async (bugId, attachment) => {
        try {
            const response = await downloadAttachment(bugId, attachment.s3_key);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download file:', err);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const statuses = ['submitted', 'in_review', 'in_progress', 'resolved', 'closed'];

    return (
        <div className="admin-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h1 className="dashboard-title">Admin Dashboard</h1>
                    <p className="dashboard-subtitle">Manage bug reports and communicate with users</p>
                </div>
                <div className="header-actions">
                    <button 
                        className="btn btn-secondary"
                        onClick={() => setShowBulkEmail(true)}
                    >
                        Send Bulk Email
                    </button>
                    <button className="btn btn-outline" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-banner">
                    {error}
                    <button onClick={() => setError('')}>×</button>
                </div>
            )}

            <div className="tabs-container">
                <button 
                    className={`tab ${activeTab === 'bugs' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bugs')}
                >
                    Bug Reports
                </button>
                <button 
                    className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                >
                    User Management
                </button>
            </div>

            <div className="dashboard-content" style={{ display: activeTab === 'bugs' ? 'block' : 'none' }}>
                <div className="filters-bar">
                    <label className="filter-label">Filter by Status:</label>
                    <select 
                        className="filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        {statuses.map(status => (
                            <option key={status} value={status}>
                                {status.replace('_', ' ')}
                            </option>
                        ))}
                    </select>
                    <span className="bug-count">{bugs.length} bug(s) found</span>
                </div>

                {isLoading ? (
                    <div className="loading">Loading bug reports...</div>
                ) : bugs.length === 0 ? (
                    <div className="no-bugs">No bug reports found.</div>
                ) : (
                    <div className="bugs-table-container">
                        <table className="bugs-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>App</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bugs.map(bug => (
                                    <tr key={bug.id}>
                                        <td className="bug-id-cell">#{bug.id.slice(0, 8)}</td>
                                        <td>{bug.software}</td>
                                        <td className="email-cell">{bug.email}</td>
                                        <td>
                                            <select
                                                className="status-select"
                                                value={bug.status}
                                                onChange={(e) => handleStatusChange(bug.id, e.target.value)}
                                                style={{ borderColor: getStatusColor(bug.status) }}
                                            >
                                                {statuses.map(status => (
                                                    <option key={status} value={status}>
                                                        {status.replace('_', ' ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="date-cell">{formatDate(bug.created_at)}</td>
                                        <td>
                                            <button 
                                                className="btn btn-small"
                                                onClick={() => setSelectedBug(bug)}
                                            >
                                                View / Reply
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedBug && activeTab === 'bugs' && (
                <div className="modal-overlay" onClick={() => setSelectedBug(null)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Bug Report Details</h2>
                            <button className="modal-close" onClick={() => setSelectedBug(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="bug-detail-grid">
                                <div className="detail-item">
                                    <span className="detail-label">Bug ID</span>
                                    <span className="detail-value">{selectedBug.id}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Status</span>
                                    <span className="detail-value" style={{ color: getStatusColor(selectedBug.status), fontWeight: 600 }}>
                                        {selectedBug.status.replace('_', ' ')}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Created</span>
                                    <span className="detail-value">{formatDate(selectedBug.created_at)}</span>
                                </div>
                            </div>

                            <h3 className="detail-section-title">Device Information</h3>
                            <div className="bug-detail-grid">
                                <div className="detail-item">
                                    <span className="detail-label">Phone Model</span>
                                    <span className="detail-value">{selectedBug.phone_model}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">System Version</span>
                                    <span className="detail-value">{selectedBug.system_version}</span>
                                </div>
                                {selectedBug.security_version && (
                                    <div className="detail-item">
                                        <span className="detail-label">Security Version</span>
                                        <span className="detail-value">{selectedBug.security_version}</span>
                                    </div>
                                )}
                                {selectedBug.kaios_api_level && (
                                    <div className="detail-item">
                                        <span className="detail-label">KaiOS API Level</span>
                                        <span className="detail-value">{selectedBug.kaios_api_level}</span>
                                    </div>
                                )}
                            </div>

                            <h3 className="detail-section-title">Bug Details</h3>
                            <div className="bug-detail-grid">
                                <div className="detail-item">
                                    <span className="detail-label">App</span>
                                    <span className="detail-value">{selectedBug.software}</span>
                                </div>
                                <div className="detail-item full-width">
                                    <span className="detail-label">Description</span>
                                    <p className="detail-value">{selectedBug.bug_description}</p>
                                </div>
                                {selectedBug.steps_to_reproduce && (
                                    <div className="detail-item full-width">
                                        <span className="detail-label">Steps to Reproduce</span>
                                        <p className="detail-value">{selectedBug.steps_to_reproduce}</p>
                                    </div>
                                )}
                                {selectedBug.attachments && selectedBug.attachments.length > 0 && (
                                    <div className="detail-item full-width">
                                        <span className="detail-label">Attachments</span>
                                        <div className="attachments-list">
                                            {selectedBug.attachments.map((att, idx) => (
                                                <button
                                                    key={idx}
                                                    className="attachment-link"
                                                    onClick={() => handleDownload(selectedBug.id, att)}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                                                        <polyline points="13 2 13 9 20 9"></polyline>
                                                    </svg>
                                                    <span>{att.filename}</span>
                                                    <span className="attachment-size">({formatFileSize(att.size)})</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <h3 className="detail-section-title">Contact Information</h3>
                            <div className="bug-detail-grid">
                                <div className="detail-item">
                                    <span className="detail-label">Email</span>
                                    <span className="detail-value">{selectedBug.email}</span>
                                </div>
                                {selectedBug.name && (
                                    <div className="detail-item">
                                        <span className="detail-label">Name</span>
                                        <span className="detail-value">{selectedBug.name}</span>
                                    </div>
                                )}
                            </div>

                            {selectedBug.admin_replies && selectedBug.admin_replies.length > 0 && (
                                <div className="previous-replies">
                                    <h3>Previous Replies</h3>
                                    {selectedBug.admin_replies.map((reply, index) => (
                                        <div key={index} className="reply-item">
                                            <p>{reply.message}</p>
                                            <span className="reply-date">{formatDate(reply.created_at)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <form onSubmit={handleReply} className="reply-form">
                                <h3>Send Reply</h3>
                                <textarea
                                    className="reply-textarea"
                                    placeholder="Type your reply here... This will be sent to the user's email."
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    rows={4}
                                    required
                                />
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={isReplying || !replyMessage.trim()}
                                >
                                    {isReplying ? 'Sending...' : 'Send Reply'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showBulkEmail && activeTab === 'bugs' && (
                <div className="modal-overlay" onClick={() => setShowBulkEmail(false)}>
                    <div className="modal modal-large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Send Bulk Email</h2>
                            <button className="modal-close" onClick={() => setShowBulkEmail(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleBulkEmail} className="bulk-email-form">
                                <div className="form-group">
                                    <label className="form-label">Subject</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Email subject"
                                        value={bulkEmailData.subject}
                                        onChange={(e) => setBulkEmailData({...bulkEmailData, subject: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Message</label>
                                    <textarea
                                        className="form-textarea"
                                        placeholder="Email message content"
                                        value={bulkEmailData.message}
                                        onChange={(e) => setBulkEmailData({...bulkEmailData, message: e.target.value})}
                                        rows={6}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <div className="email-selection-header">
                                        <label className="form-label">
                                            Select Recipients ({selectedEmails.length} of {allEmails.length} selected)
                                        </label>
                                        <button 
                                            type="button"
                                            className="btn btn-small btn-outline"
                                            onClick={handleSelectAllEmails}
                                        >
                                            {selectedEmails.length === allEmails.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                    <div className="email-selection-list">
                                        {allEmails.length === 0 ? (
                                            <p className="no-emails">No emails found in database.</p>
                                        ) : (
                                            allEmails.map(email => (
                                                <label key={email} className="email-checkbox-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedEmails.includes(email)}
                                                        onChange={() => handleToggleEmail(email)}
                                                    />
                                                    <span className="email-text">{email}</span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                    <p className="email-selection-hint">
                                        {selectedEmails.length === 0 
                                            ? 'No emails selected - will send to ALL users' 
                                            : `Will send to ${selectedEmails.length} selected email(s)`}
                                    </p>
                                </div>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary btn-full"
                                    disabled={isSendingBulk}
                                >
                                    {isSendingBulk ? 'Sending...' : `Send Email${selectedEmails.length === 0 ? ' to All' : ` to ${selectedEmails.length} Recipients`}`}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'users' && <UserManagement />}
            
        </div>
    );
}

export default AdminDashboard;
